import {Component, createRef} from "react";
import {connect} from "react-redux";
import classNames from "classnames";
import _ from "lodash";
import * as Api from "utils/api/api";
import {DETAILS_VIEW_TYPE} from "utils/constants";
import {handleError} from "utils/errorHandle.function";
import {snowplowTrackEvent} from "utils/snowplow";
import {launchDarkly} from "utils/launchDarkly";
import * as reduxSelectors from "components/store/application.reducers";
import {
    refreshTaskDetails,
    hasEditedDocument,
    updateDocumentVersion,
    getTaskDocumentData,
    integrationIframeHasUnsavedChanges,
    iframeHasVBNXTFlag
} from "components/taskdetails-remake/taskDetails.action";
import {updateDocumentKey} from "components/documentoverview/documentOverview.action";
import {PATH_TO_DOCUMENT_OVERVIEW_DETAILS} from "components/router/paths";
import {setTaskWaitingWSUpdate} from "components/mytasks/myTasks.action";
import {Loader, LoaderSize} from "components/common/Loader.component";
import {Notification, NotificationType} from "components/common/Notification.component";
import type {EditorMessageData, EditorProps, EditorUserConfiguration} from "types/editor";
import {postToIframe, registerEditorCommandSender} from "./editorCommands";

const CONFIRMATION = "CONFIRMATION";

/** the api's requests can be cancelled, which the editor does with the ones still pending when it unmounts */
type CancellableRequest = Promise<void> & { cancel: () => void };

interface DocumentEditorStateProps {
    userConfiguration: EditorUserConfiguration;
}

interface DocumentEditorDispatchProps {
    refreshTaskDetails: (taskId: string, uid?: string, isCompelloTask?: boolean) => void;
    hasEditedDocument: () => void;
    updateDocumentKey: (taskId: string, documentKey: string) => void;
    updateDocumentVersion: (version: number) => void;
    getTaskDocumentData: (taskId: string, isDocument: boolean) => void;
    integrationIframeHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    iframeHasVBNXTFlag: (supportsSaveAndDiscard?: boolean) => void;
    setTaskWaitingWSUpdate: (taskId: string) => void;
}

export type DocumentEditorProps = EditorProps & DocumentEditorStateProps & DocumentEditorDispatchProps;

interface DocumentEditorState {
    documentVersion: number | null | undefined;
    /** the editor's url, once the backend has sent it */
    editor: string | null;
    editorLoadingError: boolean;
    editorState: string | null | undefined;
    editing: boolean;
    iframeLoadStart: number;
}

const initialState = (): DocumentEditorState => ({
    documentVersion: null,
    editor: null,
    editorLoadingError: false,
    editorState: null,
    editing: false,
    iframeLoadStart: (+new Date())
});

/**
 * The editor the sending system supplies, in an iframe the backend gives the url of with the edit request
 * substituted into it. It follows what the editor posts back: while the document is edited the page asks
 * before it is left, and once a newer version is saved the task (or document) is updated to it.
 */
export class DocumentEditor extends Component<DocumentEditorProps, DocumentEditorState> {
    private readonly requests: CancellableRequest[] = [];
    private unregisterCommandSender?: () => void;
    private readonly editorRef = createRef<HTMLDivElement>();
    private readonly editorIframeRef = createRef<HTMLIFrameElement>();
    private readonly useWaitForCompello: boolean = launchDarkly.userClient?.variation("fe-wait-for-compello", false);

    constructor(props: DocumentEditorProps) {
        super(props);
        this.state = initialState();
        this.receiveMessage = this.receiveMessage.bind(this);
        this.stopEditing = this.stopEditing.bind(this);
        this.onLoadIframe = this.onLoadIframe.bind(this);
        this.sendMessageToEditor = this.sendMessageToEditor.bind(this);
    }

    initialize(): CancellableRequest {
        const loadTimeStart = +new Date();

        const apiCallMethod = this.props.viewType === DETAILS_VIEW_TYPE.DOCUMENT ? Api.getDocumentOverviewEditor : Api.getDocumentEditor;

        return apiCallMethod(this.props.taskId, this.props.companyId).then((response: { url: string }) => {
            const loadTime = (+new Date()) - loadTimeStart;

            const companyId = (this.props.viewType === DETAILS_VIEW_TYPE.TASK ? this.props.companyId : this.props.userConfiguration.currentContext) + "";
            const editRequest = {
                companyId,
                systemId: this.props.externalAppId + "",
                userId: this.props.userConfiguration.currentUserId + "",
                readonly: this.props.isReadOnly, //history task open editor in view mode only
                administrator: this.props.userConfiguration.userRoles.systemAdministrator,
                taskId: this.props.id
            };

            snowplowTrackEvent({
                category: "document_editor",
                action: "get_external_editor",
                label: "execution_time",
                // snowplowTrackEvent takes a number too; only its JS default types value as a string
                value: loadTime as unknown as string,
                property: this.props.externalApplicationName
            }, this.props.userConfiguration);

            this.setState({
                editor: _.template(response.url)({
                    editRequest: encodeURIComponent(JSON.stringify(editRequest))
                }),
                editorLoadingError: false
            }, () => {
                console.log(this.editorRef.current);
            });
        }).catch(() => {
            this.setState({
                editorLoadingError: true
            });
        });
    }

    componentDidMount() {
        window.addEventListener("message", this.receiveMessage, false);
        this.unregisterCommandSender = registerEditorCommandSender(
            (message) => postToIframe(this.editorIframeRef.current, message));

        this.requests.push(this.initialize());
    }

    componentDidUpdate(prevProps: DocumentEditorProps) {
        if (prevProps.taskId !== this.props.taskId) {
            this.setState(initialState());

            this.requests.push(this.initialize());
        }
    }

    sendMessageToEditor(message: unknown) {
        //send message just for compello editor
        if (this.state.editor?.includes("compello")) {
            this.editorIframeRef.current?.contentWindow?.postMessage({event: message}, this.state.editor);
        }
    }

    stopEditing(version: number) {
        const skipWorklowRestart = CONFIRMATION === this.props.reapproveType;

        window.removeEventListener("beforeunload", this.preventWindowClose);
        window.removeEventListener("unload", this.preventWindowClose);

        //this needs to happen here because of the navigation blocker in task details
        this.props.integrationIframeHasUnsavedChanges(false);

        if (this.props.viewType === DETAILS_VIEW_TYPE.DOCUMENT) {
            Api.updateDocument(this.props.taskId, version, skipWorklowRestart, this.props.companyId).then((response: { result: string }) => {
                this.props.updateDocumentKey(this.props.taskId, response.result);
                this.props.route(PATH_TO_DOCUMENT_OVERVIEW_DETAILS + response.result, {replace: true});
            }, (error: unknown) => {
                handleError(error);
            });
        } else {
            this.props.updateDocumentVersion(version);
            Api.updateTask(this.props.taskId, version, skipWorklowRestart).then(() => {
                this.props.refreshTaskDetails(this.props.taskId, this.props.uid, this.props.isCompelloTask);
                this.props.getTaskDocumentData(this.props.taskId, this.props.viewType === DETAILS_VIEW_TYPE.DOCUMENT);
            }, (error: unknown) => {
                handleError(error);
            });
        }
    }

    onLoadIframe() {
        const loadTime = (+new Date()) - this.state.iframeLoadStart;

        snowplowTrackEvent({
            category: "document_editor",
            action: "load_external_editor_iframe",
            label: "execution_time",
            // snowplowTrackEvent takes a number too; only its JS default types value as a string
            value: loadTime as unknown as string,
            property: this.props.externalApplicationName
        }, this.props.userConfiguration);
    }

    componentWillUnmount() {
        this.requests.forEach(p => p.cancel());
        this.unregisterCommandSender?.();
        window.removeEventListener("message", this.receiveMessage);
        window.removeEventListener("beforeunload", this.preventWindowClose);
        window.removeEventListener("unload", this.preventWindowClose);
    }

    preventWindowClose = (event: Event) => {
        event.preventDefault();
        (event as BeforeUnloadEvent).returnValue = true;
        return "";
    };

    receiveMessage(event: MessageEvent<EditorMessageData>) {
        const {editor} = this.state;

        if (editor && event.origin && editor.startsWith(event.origin)) {
            switch (event.data.event) {
                case "ready":
                case "viewing":
                    this.handleReadyOrViewingEvent(event);
                    break;

                case "editing":
                    this.handleEditingEvent(event);
                    break;

                default:
                    break;
            }
        }
    }

    handleReadyOrViewingEvent(event: MessageEvent<EditorMessageData>) {
        const {editing, documentVersion} = this.state;

        if (!this.props.isReadOnly && editing) {
            if (event.data.version && event.data.version > documentVersion) {
                if (this.useWaitForCompello && this.props.isCompelloTask) {
                    document.getElementById("task-details-wrapper")?.focus();
                    this.props.setTaskWaitingWSUpdate(this.props.taskId);
                }
                this.updateEditorState(event.data.version, false, event.data.event);
                this.stopEditing(event.data.version);
                this.props.hasEditedDocument();
                return;
            } else {
                this.props.integrationIframeHasUnsavedChanges(false);
            }
        }

        this.updateEditorState(event.data.version, false, event.data.event);
        this.props.integrationIframeHasUnsavedChanges(false);
    }

    handleEditingEvent(event: MessageEvent<EditorMessageData>) {
        const vbnxtFlag = event.data.supportsSaveAndDiscard;

        this.props.iframeHasVBNXTFlag(vbnxtFlag);
        this.props.integrationIframeHasUnsavedChanges(true);

        window.addEventListener("beforeunload", this.preventWindowClose);
        window.addEventListener("unload", this.preventWindowClose);

        this.setState({
            editorState: event.data.event,
            editing: true
        });
    }

    updateEditorState(version: number | undefined, editing: boolean, state: string | undefined) {
        this.setState({
            documentVersion: version,
            editing,
            editorState: state
        });
    }

    render() {
        const frameClasses = classNames("external-editor__frame document-editor-frame", {"document-editor-popup": this.props.isPopup});

        return (
            <div className="external-editor__surface" ref={this.editorRef}>
                {this.state.editor === null && !this.state.editorLoadingError &&
                    <div className={frameClasses}>
                        <div className="external-editor__loader">
                            <Loader size={LoaderSize.Medium}/>
                        </div>
                    </div>
                }
                {this.state.editorLoadingError &&
                    <div className={frameClasses}>
                        <div className="external-editor__message">
                            <Notification
                                type={NotificationType.ERROR}
                                title={this.props.translate("popUp.documentEditor.errorTitle")}
                                description={this.props.translate("popUp.documentEditor.errorSuggestion")}
                            />
                        </div>
                    </div>
                }

                {(this.state.editor !== null) && !this.state.editorLoadingError &&
                    <iframe
                        ref={this.editorIframeRef}
                        id={"Document-Editor-Iframe"}
                        src={this.state.editor}
                        title={"Document Editor"}
                        onLoad={this.onLoadIframe}
                        {...this.props.externalApplicationName === "Visma Business Cloud" && {allow: "clipboard-read; clipboard-write"}}
                        className={frameClasses}
                    >
                    </iframe>
                }
            </div>
        );
    }
}

const mapStateToProps = (store: unknown): DocumentEditorStateProps => ({
    userConfiguration: reduxSelectors.getUsersData(store)
});

const connected = connect(mapStateToProps, {
    refreshTaskDetails,
    hasEditedDocument,
    updateDocumentKey,
    updateDocumentVersion,
    getTaskDocumentData,
    integrationIframeHasUnsavedChanges,
    iframeHasVBNXTFlag,
    setTaskWaitingWSUpdate
})(DocumentEditor);

export default connected;
